import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { isProblem, type ChannelLimits, type ChannelLink } from '@/shared/api'
import {
  getChannelCascadeOptions,
  getChannelCatalogOptions,
  getChannelLimitsOptions,
  getQrConnectOptions,
  listBotLinksOptions,
  listChannelEventsInfiniteOptions,
  listChannelEventsOptions,
  listChannelsOptions,
  nextCursorParam,
  POLLING,
  STALE,
  type ChannelEventList,
  type GetQrConnectData,
  type ListChannelEventsData,
  type ListChannelsData,
} from '@/shared/api'

type ChannelsQueryParams = NonNullable<ListChannelsData['query']>
type ChannelEventsQueryParams = NonNullable<ListChannelEventsData['query']>

export type ChannelsStatus = NonNullable<ChannelsQueryParams['status']>
export type ChannelsType = NonNullable<ChannelsQueryParams['type']>
export type ChannelsFilter = ChannelsQueryParams
export type ChannelEventsCursor = NonNullable<ChannelEventsQueryParams['cursor']>
export type QrConnectLinkId = GetQrConnectData['path']['linkId']

/** Опрос списка: только пока в нём есть незавершённый канал — его вход может довершиться сам. */
export function channelsPollInterval(channels: ReadonlyArray<{ status: string }> | undefined): number | false {
  return (channels ?? []).some((channel) => channel.status === 'pending') ? POLLING.pendingChannels : false
}

export function channelsQuery(filter: ChannelsFilter = {}) {
  return queryOptions({
    ...listChannelsOptions({ query: filter }),
    staleTime: STALE.channels,
    refetchInterval: (query) => channelsPollInterval(query.state.data),
  })
}

/**
 * Бюджет: длина сообщения, предел подписи кнопки и то, что остаётся автору на каждом экране бота.
 *
 * ⚠️ ЖИВЁТ В ДОМЕНЕ КАНАЛОВ, А НЕ КОНТЕНТА, хотя читает его редактор текстов. Предел
 * принадлежит каналу и установке, а не тексту: три числа жили в каталоге авторинга
 * контента и переехали сюда целиком (ответ бекенда 21.08, §2). Два источника одного
 * числа — два ответа на один вопрос, и один из них однажды устареет молча.
 *
 * Ответ один на всю установку (самый строгий из каналов — разбор у самой ручки в
 * контракте) и от экрана к экрану не меняется — берём тот же `STALE.channels`.
 */
export function channelLimitsQuery() {
  return queryOptions({
    ...getChannelLimitsOptions(),
    staleTime: STALE.channels,
    /*
     * ⚠️ ЗАЩИТА ГРАНИЦЫ, А НЕ СЛОЙ СОВМЕСТИМОСТИ. С появлением `screens[]` в ответе есть вложенные
     * СПИСКИ, и читаем мы их методами (`.find`): Go отдаёт nil-слайс как `null`, а с `omitempty`
     * ключ исчезает вовсе. Типы объявляют оба поля обязательными и non-nullable — то есть молчат;
     * прод на этом падал дважды (08.08), и вся доктрина `wire-arrays.ts` написана про это.
     *
     * Пустой экран — не выдумка: у канала, где клавиатуры нет вовсе, `screens` законно пуст.
     */
    select: (data: ChannelLimits): ChannelLimits => ({
      ...data,
      screens: (data.screens ?? []).map((screen) => ({ ...screen, packing: screen.packing ?? [] })),
    }),
  })
}

/**
 * Боты компании и ссылки на них (3.5). Company-scoped: набор один на всю компанию, поэтому запрос
 * общий — сколько бы карточек броней ни открыли, читается он один раз.
 */
export function botLinksQuery() {
  return queryOptions({
    ...listBotLinksOptions(),
    staleTime: STALE.botLinks,
  })
}

export function channelCatalogQuery() {
  return queryOptions({
    ...getChannelCatalogOptions(),
    staleTime: STALE.channelCatalog,
  })
}

export function channelEventsQuery(cursor?: ChannelEventsCursor) {
  return queryOptions({
    ...listChannelEventsOptions(cursor ? { query: { cursor } } : undefined),
    staleTime: STALE.channelEvents,
    refetchInterval: POLLING.channelEvents,
  })
}

export function channelEventsInfiniteQuery() {
  return infiniteQueryOptions({
    ...listChannelEventsInfiniteOptions(),
    initialPageParam: {},
    getNextPageParam: (lastPage: ChannelEventList) => nextCursorParam(lastPage.nextCursor),
    staleTime: STALE.channelEvents,
  })
}

export function channelCascadeQuery() {
  return queryOptions({
    ...getChannelCascadeOptions(),
    staleTime: STALE.channelCascade,
  })
}

/**
 * Как часто спрашивать сессию QR: как велит сервер (`pollIntervalMs`), и не спрашивать вовсе, когда
 * она кончилась — подключена, истекла, провалилась или её больше нет (404): дальше её двигает только
 * нажатие. Своё число — лишь до первого ответа.
 */
export function qrPollInterval(link: ChannelLink | undefined, error: unknown = null): number | false {
  if (isProblem(error) && error.status === 404) return false
  if (!link) return POLLING.qrConnect
  if (link.state === 'connected' || link.state === 'expired' || link.state === 'failed') return false
  // Защита границы: ноль или мусор вместо числа закрутили бы опрос без паузы.
  return Math.max(link.pollIntervalMs || POLLING.qrConnect, 500)
}

export function qrConnectQuery(linkId: QrConnectLinkId) {
  return queryOptions({
    ...getQrConnectOptions({ path: { linkId } }),
    staleTime: STALE.live,
    refetchInterval: (query) => qrPollInterval(query.state.data, query.state.error),
  })
}
